// src/utils/formattedText.js
// ---------------------------------------------------------------------------
// The proposal formatting tags, read the same way everywhere.
//
//   ((b)) bold line      ((i)) italic line
//   ((r)) regular line   ((-)) bullet line
//
// Tags are case-insensitive ((B)), ((R)) ... and may have spaces before them.
//
// Two things made descriptions show up "jumbled" outside the proposal pages:
//   1. Most pages had their own little parser that knew ((b)) ((i)) ((-)) but
//      not ((r)), so every ((r)) line printed its tag.
//   2. Several popups printed the raw text inside a <p>. HTML squashes the line
//      breaks, so the whole description ran together on one line, tags and all.
//
// This splits on line breaks AND in front of any tag that sits mid-line, so
// text that was already flattened onto one line still comes apart correctly.
// ---------------------------------------------------------------------------

const TAG_RE = /\(\(\s*([bBiIrR-])\s*\)\)/;
const TAG_RE_GLOBAL = /\(\(\s*([bBiIrR-])\s*\)\)/g;

const typeForTag = (tag) => {
  const t = String(tag).toLowerCase();
  if (t === "b") return "bold";
  if (t === "i") return "italic";
  if (t === "r") return "regular";
  return "bullet";
};

/**
 * Text -> [{ type, text }]
 * type: "bold" | "italic" | "regular" | "bullet" | "plain" | "blank"
 * "plain" is a line with no tag. Pages decide how to show it (proposal pages
 * have always shown untagged lines as bullets).
 */
export const parseFormattedLines = (input) => {
  if (input === null || input === undefined) return [];
  const text = String(input).replace(/\r\n?/g, "\n");
  const out = [];

  text.split("\n").forEach((rawLine) => {
    if (!rawLine.trim()) {
      out.push({ type: "blank", text: "" });
      return;
    }

    // Break the line in front of every tag, wherever it sits. split() with a
    // capturing group returns [lead, tag, body, tag, body, ...].
    const pieces = [];
    const parts = rawLine.split(TAG_RE_GLOBAL);
    const lead = parts[0].trim();
    if (lead) pieces.push({ type: "plain", text: lead });
    for (let i = 1; i < parts.length; i += 2) {
      pieces.push({ type: typeForTag(parts[i]), text: (parts[i + 1] || "").trim() });
    }

    pieces.forEach((p) => {
      // A tag with nothing after it ("((r))" alone) is just spacing.
      if (!p.text) out.push({ type: "blank", text: "" });
      else out.push(p);
    });
  });

  // Collapse runs of blank lines and trim blanks at both ends.
  const collapsed = [];
  out.forEach((line) => {
    if (line.type === "blank" && (collapsed.length === 0 || collapsed[collapsed.length - 1].type === "blank")) {
      return;
    }
    collapsed.push(line);
  });
  while (collapsed.length && collapsed[collapsed.length - 1].type === "blank") collapsed.pop();

  return collapsed;
};

/** True when the text uses any of the tags. */
export const hasFormattingTags = (text) => TAG_RE.test(String(text || ""));

/**
 * Plain text version, for places that cannot show bold (PDF text, emails,
 * tooltips, invoice lines). Keeps one item per line.
 *   untagged: "plain" (default) or "bullet" - how lines with no tag appear.
 */
export const formattedToPlainText = (text, { untagged = "plain" } = {}) =>
  parseFormattedLines(text)
    .map((line) => {
      if (line.type === "blank") return "";
      if (line.type === "bullet") return `• ${line.text}`;
      if (line.type === "plain" && untagged === "bullet") return `• ${line.text}`;
      return line.text;
    })
    .join("\n");

/** One-line version for table cells and short previews. */
export const formattedToOneLine = (text, maxLength = 0) => {
  const one = parseFormattedLines(text)
    .filter((line) => line.type !== "blank")
    .map((line) => (line.type === "bullet" ? `• ${line.text}` : line.text))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  if (maxLength && one.length > maxLength) return `${one.slice(0, maxLength)}...`;
  return one;
};

/** Strip tags without restructuring (for search text and the like). */
export const stripFormattingTags = (text) =>
  String(text || "").replace(TAG_RE_GLOBAL, " ").replace(/[ \t]+/g, " ").trim();
